import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { X, ShoppingCart, User, Search, LogOut, Settings, Home, Package, Grid, Heart, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { truncateString } from '@/lib/utils';

import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import AuthModal from '@/components/AuthModal';
import StickySearchBar from '@/components/StickySearchBar';

import MobileTopNavigation from '@/components/MobileTopNavigation';
import { useAuth } from '@/contexts/AuthContext';
import { useCart } from '@/contexts/CartContext';

const Navigation = () => {
  const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [authMode, setAuthMode] = useState<'login' | 'signup'>('login');
  const { user, profile, isAdmin, signOut, loading } = useAuth();
  const { totalItems, openCart, navigateToCart, isCheckoutActive } = useCart();
  const navigate = useNavigate();
  const location = useLocation();

  // Show loading state while auth is initializing
  if (loading) {
    return (
      <nav className="fixed top-0 w-full bg-background/95 backdrop-blur-md border-b border-border z-50 shadow-sm">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            <div className="animate-pulse bg-muted h-8 w-32 rounded"></div>
            <div className="animate-pulse bg-muted h-8 w-24 rounded"></div>
          </div>
        </div>
      </nav>
    );
  }

  const menuItems = [
    { label: 'בית', href: '/', icon: Home },
    { label: 'קטגוריות', href: '/products', icon: Package },
    { label: 'אודות', href: '/about', icon: Heart },
  ];

  return (
    <>
      {/* Beta Ribbon - Fixed to top-right corner (Desktop only, height matches header) */}
      <div className="hidden lg:block fixed top-0 right-0 z-[100] pointer-events-none overflow-hidden w-32 h-16">
        <span className="absolute top-3 -right-8 bg-red-500 text-white text-[11px] font-bold px-12 py-1 shadow-lg whitespace-nowrap transform rotate-45 origin-center">
          בהרצה
        </span>
      </div>

      <nav className="hidden lg:block fixed top-0 w-full bg-background/95 backdrop-blur-md border-b border-border z-50 shadow-sm">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            {/* Desktop Logo */}
            <Link to="/" className="relative flex items-center hover:opacity-80 transition-opacity">
              <img
                src="/uploads/ad1ab8aa-6b4f-439f-9472-027351fbb264.png"
                alt="Excess Market"
                className="h-10 w-auto"
                loading="eager"
                fetchpriority="high"
                sizes="40px"
                srcSet="/uploads/ad1ab8aa-6b4f-439f-9472-027351fbb264.png 1009w"
              />
            </Link>

            {/* Desktop Menu */}
            <div className="flex items-center gap-16">
              {menuItems.map((item) => {
                const isActive = location.pathname === item.href;


                return (
                  <Link
                    key={item.label}
                    to={item.href}
                    className={`relative text-foreground font-medium transition-colors hover:text-[#8dcf2b] after:content-[''] after:absolute after:w-full after:h-0.5 after:bottom-[-4px] after:left-0 after:bg-[#8dcf2b] after:scale-x-0 after:origin-center after:transition-transform after:duration-300 hover:after:scale-x-100 flex items-center gap-2 ${isActive
                      ? 'text-[#8dcf2b] after:scale-x-100'
                      : ''
                      }`}
                  >
                    <item.icon className="h-4 w-4" />
                    {item.label}
                  </Link>
                );
              })}
            </div>

            {/* Desktop Actions */}
            <div className="flex items-center gap-6">
              {/* Favorites */}
              <Button variant="ghost" size="sm" onClick={() => navigate('/favorites')}>
                <Heart className="h-4 w-4" />
              </Button>

              {/* Cart */}
              <Button
                variant="ghost"
                size="sm"
                className="relative"
                onClick={isCheckoutActive ? navigateToCart : openCart}
                aria-label={`עגלת קניות - ${totalItems} פריטים`}
              >
                <ShoppingCart className="h-5 w-5" />
                {totalItems > 0 && (
                  <Badge className="absolute -top-2 -right-2 h-5 w-5 flex items-center justify-center p-0 bg-destructive text-destructive-foreground text-xs">
                    {totalItems > 99 ? '99+' : totalItems}
                  </Badge>
                )}
              </Button>

              {/* Search */}
              <Button variant="ghost" size="sm" onClick={() => setIsSearchOpen(true)}>
                <Search className="h-4 w-4" />
              </Button>

              {/* User Account */}
              {user ? (
                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="sm" className="flex items-center gap-2 w-[120px] justify-start">
                      <User className="h-4 w-4 flex-shrink-0" />
                      <span className="whitespace-nowrap truncate">{truncateString(profile?.first_name, 8) || 'חשבון'}</span>
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end" className="w-48" sideOffset={5}>
                    <div dir="rtl" className="w-full">
                      <DropdownMenuItem onClick={() => navigate('/profile')} className="flex items-center gap-2">
                        <Settings className="h-4 w-4" />
                        <span>חשבון שלי</span>
                      </DropdownMenuItem>
                      {isAdmin && (
                        <DropdownMenuItem onClick={() => navigate('/admin')} className="flex items-center gap-2">
                          <Settings className="h-4 w-4" />
                          <span>לוח ניהול</span>
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuSeparator />
                      <DropdownMenuItem onClick={signOut} className="flex items-center gap-2">
                        <LogOut className="h-4 w-4" />
                        <span>התנתק</span>
                      </DropdownMenuItem>
                    </div>
                  </DropdownMenuContent>
                </DropdownMenu>
              ) : (
                <Button
                  size="sm"
                  className="bg-lime text-lime-foreground hover:bg-lime/90"
                  onClick={() => {
                    setAuthMode('login');
                    setIsAuthModalOpen(true);
                  }}
                >
                  התחברות
                </Button>
              )}
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Top Navigation */}
      <MobileTopNavigation
        onSearchClick={() => setIsSearchOpen(!isSearchOpen)}
        onAuthClick={() => {
          setAuthMode('login');
          setIsAuthModalOpen(true);
        }}
        isCheckoutPage={location.pathname === '/checkout'}
      />

      {/* Sticky Search Bar */}
      <StickySearchBar
        isOpen={isSearchOpen}
        onClose={() => setIsSearchOpen(false)}
      />

      {/* Auth Modal */}
      <AuthModal
        isOpen={isAuthModalOpen}
        onClose={() => setIsAuthModalOpen(false)}
        initialMode={authMode}
      />

    </>
  );
};

export default Navigation;